/**
 * The one client-side path to post money (C19): `preview` (server plans, dry run) → PIN dialog
 * (D-018; own or a supervisor's on the same screen) → `commit` with the preview id and a fresh
 * idempotency key (ADR-02 §2.6). Nothing is computed here: the figures a screen shows before the PIN
 * are the preview's `derived` strings (ADR-08 §2.1 item 2). A commit held for second-person approval
 * (D-035) comes back `pending`; the maker is told and the approvers are notified by the server.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { CommitResult, NewCustomerAccountSpec, PreviewResult } from "../api/types.js";
import { useApp } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";
import { usePinGate, type PinGate } from "./pin-gate.js";
import { keys } from "./query.js";
import { useToast } from "./toast.js";
import { useSession } from "./context.js";

export interface GatewayRunOptions {
  /** Message shown in the PIN dialog (what the PIN confirms). */
  readonly pinMessage?: string;
  /** i18n key of the success toast; default `toast.operation_posted`. */
  readonly successKey?: string;
  readonly allowSupervisor?: boolean;
}

export interface Gateway {
  readonly preview: (type: string, input: Record<string, unknown>) => Promise<PreviewResult>;
  /** preview → PIN → commit; `undefined` when the user cancelled the PIN dialog. Errors are toasted and rethrown. */
  readonly run: (type: string, input: Record<string, unknown>, opts?: GatewayRunOptions) => Promise<CommitResult | undefined>;
  /** commit an already issued preview (after the screen showed its figures). */
  readonly commitPreview: (preview: PreviewResult, opts?: GatewayRunOptions) => Promise<CommitResult | undefined>;
  /** [C22] preview with customer accounts opened inside the operation (`"new:<ref>"` in the input; D-018, D-245 (e)). The commit then needs a PIN of a `create_customers` holder (own or supervisor). */
  readonly previewWithNew: (type: string, input: Record<string, unknown>, newAccounts: readonly NewCustomerAccountSpec[]) => Promise<PreviewResult>;
  /** [C22] several previews → one PIN → one transaction (`POST /operations/commit-batch`, D-246 (c)); `undefined` when the PIN dialog was cancelled. */
  readonly commitBatch: (previews: readonly PreviewResult[], opts?: GatewayRunOptions) => Promise<readonly CommitResult[] | undefined>;
  readonly pin: PinGate;
}

export function useGateway(): Gateway {
  const { source, t } = useApp();
  const { sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: keys.accounts });
    void qc.invalidateQueries({ queryKey: keys.dashboard });
    void qc.invalidateQueries({ queryKey: keys.approvals });
    void qc.invalidateQueries({ queryKey: keys.notifications });
    void qc.invalidateQueries({ queryKey: ["statement"] });
    void qc.invalidateQueries({ queryKey: keys.dollar });
    void qc.invalidateQueries({ queryKey: ["dollar-operations"] });
    void qc.invalidateQueries({ queryKey: keys.currencies });
    void qc.invalidateQueries({ queryKey: ["currency-operations"] });
    void qc.invalidateQueries({ queryKey: ["customers"] });
    void qc.invalidateQueries({ queryKey: ["customer-statement"] });
    void qc.invalidateQueries({ queryKey: ["transfers"] });
    void qc.invalidateQueries({ queryKey: ["transfer-detail"] });
    void qc.invalidateQueries({ queryKey: keys.journal }); // [C25]
    void qc.invalidateQueries({ queryKey: keys.profits }); // [C25] every posting can change the Profits & Expenses figures
    void qc.invalidateQueries({ queryKey: keys.capitalOperations }); // [C26]
  }, [qc]);

  const preview = useCallback((type: string, input: Record<string, unknown>) => source.previewOperation(type, input), [source]);
  const previewWithNew = useCallback(
    (type: string, input: Record<string, unknown>, newAccounts: readonly NewCustomerAccountSpec[]) =>
      newAccounts.length > 0 ? source.previewOperationWithNew(type, input, newAccounts) : source.previewOperation(type, input),
    [source],
  );

  const commitPreview = useCallback(
    async (pv: PreviewResult, opts: GatewayRunOptions = {}): Promise<CommitResult | undefined> => {
      try {
        const res = await pin.withPin((proof) => source.commitOperation(pv.previewId, crypto.randomUUID(), proof), {
          ...(opts.pinMessage ? { message: opts.pinMessage } : {}),
          allowSupervisor: opts.allowSupervisor ?? true,
        });
        if (!res) return undefined;
        if (res.status === "pending") toast(t("toast.sent_for_approval"), "info");
        else toast(t(opts.successKey ?? "toast.operation_posted"), "success");
        invalidate();
        return res;
      } catch (e) {
        if (isSessionLost(e)) sessionLost();
        else toast(errorMessage(t, e), "error");
        throw e;
      }
    },
    [pin, source, toast, t, invalidate, sessionLost],
  );

  const run = useCallback(
    async (type: string, input: Record<string, unknown>, opts: GatewayRunOptions = {}): Promise<CommitResult | undefined> => {
      let pv: PreviewResult;
      try {
        pv = await preview(type, input);
      } catch (e) {
        if (isSessionLost(e)) sessionLost();
        else toast(errorMessage(t, e), "error");
        throw e;
      }
      return commitPreview(pv, opts);
    },
    [preview, commitPreview, toast, t, sessionLost],
  );

  const commitBatch = useCallback(
    async (pvs: readonly PreviewResult[], opts: GatewayRunOptions = {}): Promise<readonly CommitResult[] | undefined> => {
      try {
        const res = await pin.withPin((proof) => source.commitOperations(pvs.map((p) => ({ previewId: p.previewId, idempotencyKey: crypto.randomUUID() })), proof), {
          ...(opts.pinMessage ? { message: opts.pinMessage } : {}),
          allowSupervisor: opts.allowSupervisor ?? true,
        });
        if (!res) return undefined;
        if (res.results.some((r) => r.status === "pending")) toast(t("toast.sent_for_approval"), "info");
        else toast(t(opts.successKey ?? "toast.operation_posted"), "success");
        invalidate();
        return res.results;
      } catch (e) {
        if (isSessionLost(e)) sessionLost();
        else toast(errorMessage(t, e), "error");
        throw e;
      }
    },
    [pin, source, toast, t, invalidate, sessionLost],
  );

  return { preview, run, commitPreview, previewWithNew, commitBatch, pin };
}
